/* ==================================================================
   Holt — Judge.me "Show more" paging + gradient sizing

   WHY THIS EXISTS IN THE THEME
   Judge.me's own per-page setting lives in their app dashboard, not in
   anything the theme can reach, and its lowest useful mode still paints
   a long page. This does the paging client-side instead: show six
   reviews, reveal six more per click.

   WHY IT'S NOT JUST CSS
   :nth-child could hide the extras, but there'd be no way to reveal
   them on click without JS anyway, and the button has to know how many
   are left.

   WHAT IT ALSO DOES
   Sets --holt-reviews-bleed to the review section's real height. The
   gradient behind the reviews is the closer-look section's aurora
   stretched downward (see holt-reviews-judgeme.css); before this, that
   stretch was a fixed -100% guess that went white whenever the section
   grew. Now it's measured, so revealing more reviews extends the
   gradient with them.

   THE WIDGET RE-RENDERS ITSELF
   Judge.me's revamp widget is a Vue app: sorting, filtering and tab
   switches replace the whole review list. So this runs under a
   MutationObserver rather than once on load, and is written to be
   idempotent — it checks the state it wants before changing anything,
   which is what stops its own DOM edits from re-triggering it forever.
   ================================================================== */

(function () {
  'use strict';

  /* 6 shown, 6 more per click. Override in the console or a snippet
     with window.HOLT_REVIEWS_STEP before this file loads. */
  var STEP = window.HOLT_REVIEWS_STEP || 6;
  var LIST = '.jdgm-review-list';
  var ITEM = '.jm-review-item';
  var BTN_CLASS = 'holt-rev-more';

  var shown = STEP;
  var applying = false;

  /* ARMRA's reads a flat "Show more" — no count, no plural to get
     wrong. Measured on their page: 14px, weight 400, sentence case. */
  var LABEL = 'Show more';

  /* Each review sits in its own unclassed wrapper div inside the list.
     Hiding the wrapper rather than the item keeps the list's own
     flex/stack spacing from leaving a gap where a review used to be. */
  function wrappers(list) {
    return Array.prototype.filter.call(list.children, function (child) {
      return child.querySelector(ITEM) || child.matches(ITEM);
    });
  }

  function apply() {
    if (applying) return;

    var list = document.querySelector(LIST);
    if (!list) return;

    var rows = wrappers(list);
    if (!rows.length) return;

    applying = true;

    var limit = Math.min(shown, rows.length);

    rows.forEach(function (row, i) {
      var hide = i >= limit;
      if (row.hidden !== hide) row.hidden = hide;
      /* hidden alone loses to display:flex from the stack layout */
      row.style.display = hide ? 'none' : '';
    });

    var remaining = rows.length - limit;
    var btn = list.parentNode.querySelector('.' + BTN_CLASS);

    if (remaining > 0) {
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = BTN_CLASS;
        btn.addEventListener('click', function () {
          shown += STEP;
          apply();
          sizeGradient();
        });
        list.parentNode.insertBefore(btn, list.nextSibling);
      }
      if (btn.textContent !== LABEL) btn.textContent = LABEL;
      btn.hidden = false;
    } else if (btn) {
      btn.hidden = true;
    }

    applying = false;
  }

  /* The aurora is painted by the closer-look section and stretched down
     over the reviews. Measure what it actually has to cover instead of
     guessing a percentage. The 48px is slack so the fade-out lands just
     past the last review rather than on top of it. */
  function sizeGradient() {
    var cl = document.querySelector('.holt-cl');
    if (!cl) return;

    var section = document.querySelector('.jdgm-review-widget');
    if (!section) return;

    section = section.closest('.shopify-section');
    if (!section) return;

    var h = Math.round(section.getBoundingClientRect().height) + 48;
    cl.style.setProperty('--holt-reviews-bleed', '-' + h + 'px');
  }

  function run() {
    apply();
    sizeGradient();
  }

  function watch() {
    var host = document.querySelector('.jdgm-review-widget');
    if (!host) return false;

    var pending = null;
    new MutationObserver(function () {
      if (applying) return;
      clearTimeout(pending);
      pending = setTimeout(run, 120);
    }).observe(host, { childList: true, subtree: true });

    run();
    return true;
  }

  /* The widget is injected after DOMContentLoaded and there's no event
     to hook, so poll briefly for it and give up rather than spin. */
  function boot() {
    if (watch()) return;
    var tries = 0;
    var timer = setInterval(function () {
      if (watch() || ++tries > 40) clearInterval(timer);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('resize', function () {
    clearTimeout(window.__holtRevResize);
    window.__holtRevResize = setTimeout(sizeGradient, 150);
  });
})();
